import { ColDef, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import { t } from 'i18next';

import {
  AttributeColorSwatch,
  getAttributeValueLabel,
} from 'companySettings/components/PartFinderAttributeValues';
import {
  attributeStatusColorMap,
  attributeStatusLabelKey,
  attributeTypeLabelKey,
} from 'companySettings/constants';
import { AttributeStatus, AttributeType } from 'companySettings/enums';
import { IPartFinderAttribute } from 'companySettings/types';
import { ChipListCell, StatusCell } from 'core/elements';

import { NameCell } from './cells';

// A value as its chip in the grid shows it.
interface IValueChip {
  colorHex?: string;
  label: string;
}

const formatType = ({ value }: ValueFormatterParams) =>
  value ? t(attributeTypeLabelKey[value as AttributeType]) : '';

const formatStatus = ({ value }: ValueFormatterParams) =>
  value ? t(attributeStatusLabelKey[value as AttributeStatus]) : '';

export const getColumnDefs = (): ColDef<IPartFinderAttribute>[] => [
  {
    colId: 'name',
    headerName: t('headerName'),
    field: 'name',
    filter: 'agTextColumnFilter',
    flex: 1,
    cellRenderer: NameCell,
  },
  {
    colId: 'type',
    headerName: t('headerValueType'),
    field: 'type',
    filter: 'agSetColumnFilter',
    filterParams: { valueFormatter: formatType },
    valueFormatter: formatType,
  },
  {
    colId: 'values',
    headerName: t('headerValues'),
    field: 'values',
    sortable: false,
    filter: 'agTextColumnFilter',
    filterValueGetter: ({ data }: ValueGetterParams<IPartFinderAttribute>) =>
      data?.values?.map((value) => getAttributeValueLabel(data.type, value, t)).join(' ') ?? '',
    // A value's label depends on the list's type, so the chips get their labels here.
    valueGetter: ({ data }: ValueGetterParams<IPartFinderAttribute>) =>
      data &&
      data.values?.map(
        (value): IValueChip => ({
          colorHex: value.colorHex,
          label: getAttributeValueLabel(data.type, value, t),
        })
      ),
    flex: 2,
    // Grouping would key on the value objects, not on what the chips say.
    enableRowGroup: false,
    cellRenderer: ChipListCell,
    // What a CSV download writes for the chips.
    valueFormatter: ({ value }: ValueFormatterParams<IPartFinderAttribute, IValueChip[]>) =>
      value?.map(({ label }) => label).join(', ') ?? '',
    cellRendererParams: {
      highlightFilterMatches: true,
      fitToWidth: true,
      getChipValue: ({ label }: IValueChip) => label,
      getChipStartDecorator: ({ colorHex }: IValueChip) =>
        colorHex && <AttributeColorSwatch colorHex={colorHex} />,
    },
  },
  {
    colId: 'status',
    headerName: t('headerStatus'),
    field: 'status',
    filter: 'agSetColumnFilter',
    filterParams: { valueFormatter: formatStatus },
    // What an export writes: the label, not the raw status.
    valueFormatter: formatStatus,
    cellRenderer: StatusCell,
    cellRendererParams: {
      colorMap: attributeStatusColorMap,
      labelMap: Object.fromEntries(
        Object.entries(attributeStatusLabelKey).map(([status, labelKey]) => [status, t(labelKey)])
      ),
    },
  },
  {
    colId: 'versionNumber',
    headerName: t('headerVersion'),
    field: 'versionNumber',
    valueFormatter: ({ value }: ValueFormatterParams) =>
      value ? t('labelVersionNumber', { number: value }) : '',
  },
];
